// FieldIQ — Today tab — daily dashboard

import { useState, useEffect } from "react";
import {
  Contact,
  Shield,
  MapPin,
  Search,
  DoorOpen,
  PhoneCall,
  MessageCircle,
  Calendar,
  Activity,
  Play,
  Megaphone,
  Edit3,
  WifiOff,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { LogInteractionModal } from "../components/LogInteraction";
import { UserSurveyAlert } from "../components/UserSurvey";
import { RichText } from "../components/RichText";
import {
  EventRsvpButtons,
  RsvpCounts,
  useEventRsvp,
} from "../components/EventRsvp";
import { FIELDIQ_API_URL } from "../lib/api";
import { formatActivityDate, streakBadge } from "../lib/helpers";
import { ACTIVITY_KEY, loadAudioBlob, loadTodayActivity } from "../lib/storage";

const RSVP_LABEL = { going: "Going", maybe: "Maybe", not_going: "Declined" };
const RSVP_COLOR = {
  going: "text-emerald-400",
  maybe: "text-amber-400",
  not_going: "text-slate-500",
};

// An Upcoming Events row. Collapsed it is the summary it always was; expanded
// it shows the event body and the RSVP buttons, so responding no longer means
// leaving Today for the Activities tab. RSVP state lives here rather than in
// TodayTab because upcomingEvents is a prop owned by App — a vote would
// otherwise be overwritten on the next render.
const UpcomingEventCard = ({ ev, facility, isOnline }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(null);
  const { rsvp, goingCount, maybeCount, loading, submit } = useEventRsvp({
    apiId: ev._apiId,
    facilityId: facility?.facility_id,
    isOnline,
    event: ev,
    onToast: setNote,
  });

  return (
    <div className="bg-slate-800/40 border border-slate-700 rounded-xl">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-start gap-3 px-3 py-3 text-left"
      >
        <div className="w-7 h-7 rounded-lg bg-blue-500/15 border border-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
          {ev.subtype === "vote" ? (
            <Shield size={14} className="text-rose-400" />
          ) : (
            <Calendar size={14} className="text-blue-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p
            className={`text-white text-sm font-semibold leading-snug ${
              open ? "" : "truncate"
            }`}
          >
            {ev.title}
          </p>
          <p className="text-xs text-cyan-400 font-medium mt-0.5">
            {formatActivityDate(ev.date)}
            {ev.time && ` · ${ev.time}`}
          </p>
          {ev.location && (
            <p
              className={`text-xs text-slate-500 mt-0.5 ${
                open ? "" : "truncate"
              }`}
            >
              {ev.location}
            </p>
          )}
          <RsvpCounts
            goingCount={goingCount}
            maybeCount={maybeCount}
            className="mt-0.5"
          />
        </div>
        <span className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
          {rsvp && (
            <span className={`text-xs font-semibold ${RSVP_COLOR[rsvp]}`}>
              {RSVP_LABEL[rsvp]}
            </span>
          )}
          {open ? (
            <ChevronUp size={14} className="text-slate-500" />
          ) : (
            <ChevronDown size={14} className="text-slate-500" />
          )}
        </span>
      </button>

      {open && (
        <div className="px-3 pb-3 pt-3 space-y-3 border-t border-slate-700/60">
          {ev.description && (
            <RichText
              text={ev.description}
              className="text-xs text-slate-400 leading-relaxed"
            />
          )}
          <EventRsvpButtons
            rsvp={rsvp}
            loading={loading}
            onSelect={submit}
            compact
          />
          {note && <p className="text-xs text-slate-400">{note}</p>}
        </div>
      )}
    </div>
  );
};

export const TodayTab = ({
  user,
  facility,
  onOpenTopic,
  topicsEnabled = true,
  logInteractionEnabled = true,
  onLocReady,
  topicPrefs,
  apiTopics,
  isOnline,
  upcomingEvents = [],
  pinnedBroadcasts = [],
  onViewBroadcasts,
}) => {
  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  const [activity, setActivity] = useState(loadTodayActivity);
  const [showModal, setShowModal] = useState(false);
  const [modalInitialType, setModalInitialType] = useState("door");
  const [playingKey, setPlayingKey] = useState(null);
  const [playingUrl, setPlayingUrl] = useState(null);
  const [locError, setLocError] = useState(null);

  const allTopics = apiTopics;
  const visibleTopics = topicPrefs
    ? topicPrefs
        .map((slug) => allTopics.find((t) => (t.slug || t.id) === slug))
        .filter(Boolean)
    : allTopics.slice(0, 3);

  const handleLog = () => {
    setActivity(loadTodayActivity());
  };

  const locLookup = () => {
    if (!navigator.geolocation) {
      setLocError(
        "Can't get your location — search by name or address instead.",
      );
      return;
    }
    setLocError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onLocReady?.({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      () => {
        setLocError(
          "Can't get your location — search by name or address instead.",
        );
      },
      { timeout: 10000, maximumAge: 60000 },
    );
  };

  const searchLookup = () => {
    onLocReady?.(null);
  };

  // Poll transcript status for pending entries with backoff. Whisper plus
  // the n8n summarizer (5-minute schedule) can take several minutes; give
  // up after ~20 minutes and surface an error state with re-upload.
  const isAudioPending = (a) =>
    a.serverId &&
    (a.audioStatus === "processing" ||
      a.audioStatus === "transcribed" || // keep polling for the summary upgrade
      (!a.audioStatus && a.note === "Transcribing…")); // legacy entries

  const pendingIds = activity
    .filter(isAudioPending)
    .map((a) => a.serverId)
    .join(",");

  useEffect(() => {
    if (!pendingIds) return;
    let cancelled = false;
    let timer = null;
    // 30s, 1m, 2m, then the n8n cadence — ~20 min total
    const delays = [30e3, 60e3, 120e3, 300e3, 300e3, 300e3, 300e3];
    const TIMEOUT_MS = 20 * 60e3;

    const checkOnce = async () => {
      let rows = [];
      try {
        const token = localStorage.getItem("fieldiq_token");
        const r = await fetch(
          `${FIELDIQ_API_URL}/interactions/batch?ids=${pendingIds}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        if (r.ok) rows = await r.json();
      } catch {}
      const byId = Object.fromEntries(
        (Array.isArray(rows) ? rows : []).map((r) => [String(r.id), r]),
      );
      let changed = false;
      let stillPending = false;
      const now = Date.now();
      const all = JSON.parse(localStorage.getItem(ACTIVITY_KEY) || "[]");
      all.forEach((entry) => {
        if (!isAudioPending(entry)) return;
        const row = byId[String(entry.serverId)];
        const timedOut =
          now - new Date(entry.uploadedAt || entry.ts).getTime() > TIMEOUT_MS;
        if (row?.processing_status === "complete" && row.summary) {
          // Best case: the GPT summary landed.
          entry.note = row.summary;
          entry.audioStatus = "complete";
          changed = true;
        } else if (row?.transcript) {
          // Transcription succeeded — surface the transcript right away. Keep
          // polling for the nicer summary until timeout, but a note that has a
          // transcript must never be shown as a failure.
          if (entry.note !== row.transcript) {
            entry.note = row.transcript;
            changed = true;
          }
          if (timedOut) {
            // Summary never arrived; settle on the transcript as final.
            entry.audioStatus = "complete";
            changed = true;
          } else {
            if (entry.audioStatus !== "transcribed") {
              entry.audioStatus = "transcribed";
              changed = true;
            }
            stillPending = true;
          }
        } else if (row?.processing_status === "error") {
          // Genuine failure: transcription produced no transcript.
          entry.audioStatus = "error";
          changed = true;
        } else if (timedOut) {
          entry.audioStatus = "error";
          changed = true;
        } else {
          stillPending = true;
        }
      });
      if (changed) {
        localStorage.setItem(ACTIVITY_KEY, JSON.stringify(all));
        if (!cancelled) setActivity(loadTodayActivity());
      }
      return stillPending;
    };

    let attempt = 0;
    const loop = async () => {
      const more = await checkOnce();
      if (cancelled || !more || attempt >= delays.length) return;
      timer = setTimeout(loop, delays[attempt++]);
    };
    loop();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingIds]);

  // Re-upload from the locally cached blob after a failed/expired upload
  const [retryingKey, setRetryingKey] = useState(null);

  const handleRetryUpload = async (item) => {
    if (!item.audioKey || retryingKey) return;
    setRetryingKey(item.audioKey);
    try {
      const record = await loadAudioBlob(item.audioKey);
      if (!record) throw new Error("no local audio");
      const fd = new FormData();
      const ext = (record.mimeType || record.blob.type || "").includes("webm")
        ? "webm"
        : "mp4";
      fd.append("audio", record.blob, `recording.${ext}`);
      fd.append("facility_id", facility?.facility_id || "");
      fd.append("interaction_type", item.type);
      fd.append("person_name", item.name || "");
      fd.append("notes", item.rawNote || "");
      const token = localStorage.getItem("fieldiq_token");
      const resp = await fetch(`${FIELDIQ_API_URL}/interactions/audio`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!resp.ok) throw new Error("upload failed");
      const data = await resp.json();
      // Point the entry at the new server row and resume polling
      const all = JSON.parse(localStorage.getItem(ACTIVITY_KEY) || "[]");
      all.forEach((e) => {
        if (e.audioKey === item.audioKey) {
          e.serverId = data.id;
          e.audioStatus = "processing";
          e.note = "Transcribing…";
          e.uploadedAt = new Date().toISOString();
        }
      });
      localStorage.setItem(ACTIVITY_KEY, JSON.stringify(all));
      setActivity(loadTodayActivity());
    } catch {
      // Entry stays in error state — user can retry again
    } finally {
      setRetryingKey(null);
    }
  };

  const handlePlay = async (audioKey) => {
    if (playingKey === audioKey) {
      if (playingUrl) URL.revokeObjectURL(playingUrl);
      setPlayingKey(null);
      setPlayingUrl(null);
      return;
    }
    if (playingUrl) URL.revokeObjectURL(playingUrl);
    const record = await loadAudioBlob(audioKey);
    if (!record) return;
    const url = URL.createObjectURL(record.blob);
    setPlayingKey(audioKey);
    setPlayingUrl(url);
  };

  const counts = {
    contact: activity.filter((a) => a.type === "contact").length,
    door: activity.filter((a) => a.type === "door").length,
    call: activity.filter((a) => a.type === "call").length,
    message: activity.filter((a) => a.type === "message").length,
  };

  const LOG_TILES = [
    {
      type: "doors",
      label: "Doors",
      icon: DoorOpen,
      count: counts.door,
      tcRgb: [52, 211, 153],
      textClass: "text-emerald-400",
    },
    {
      type: "contacts",
      label: "Contacts",
      icon: Contact,
      count: counts.contact,
      tcRgb: [34, 211, 238],
      textClass: "text-cyan-400",
    },
    {
      type: "calls",
      label: "Calls",
      icon: PhoneCall,
      count: counts.call,
      tcRgb: [167, 139, 250],
      textClass: "text-violet-400",
    },
    {
      type: "messages",
      label: "Messages",
      icon: MessageCircle,
      count: counts.message,
      tcRgb: [251, 191, 36],
      textClass: "text-amber-400",
    },
  ];

  const TILE_TO_TYPE = {
    doors: "door",
    contacts: "contact",
    calls: "call",
    messages: "message",
    other: "door",
  };

  const onTapLog = (tileType) => {
    setModalInitialType(TILE_TO_TYPE[tileType] || "door");
    setShowModal(true);
  };

  const typeIconMap = {
    door: DoorOpen,
    call: PhoneCall,
    message: MessageCircle,
    contact: Contact,
  };

  return (
    <div className="space-y-5">
      {showModal && logInteractionEnabled && (
        <LogInteractionModal
          onClose={() => setShowModal(false)}
          onLog={handleLog}
          user={user}
          facility={facility}
          initialType={modalInitialType}
        />
      )}

      {!isOnline && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-900/40 border border-amber-700/50 rounded-xl text-amber-300 text-xs">
          <WifiOff size={13} />
          Offline — activity counts saved locally and will sync when
          reconnected.
        </div>
      )}

      {pinnedBroadcasts.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
              <Megaphone size={11} />
              Pinned Announcements
            </p>
            <button
              onClick={onViewBroadcasts}
              className="text-xs text-cyan-400 hover:text-cyan-300"
            >
              See all
            </button>
          </div>
          <div className="space-y-2">
            {pinnedBroadcasts.map((b) => (
              <div
                key={b.id}
                className="bg-slate-800 border border-amber-600/40 rounded-xl px-3.5 py-3"
              >
                <p className="text-xs font-semibold text-amber-400 mb-0.5 truncate">
                  📌 {b.title}
                </p>
                <p className="text-xs text-slate-300 line-clamp-2">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[19px] font-extrabold text-white leading-snug whitespace-nowrap">
            Good{" "}
            {new Date().getHours() < 12
              ? "morning"
              : new Date().getHours() < 17
                ? "afternoon"
                : "evening"}
            {user?.name
              ? `, ${user.name.split(" ")[0]}`
              : user?.first_name
                ? `, ${user.first_name}`
                : ""}
          </h1>
          <p className="text-[12.5px] font-medium text-slate-500 mt-0.5">
            {today}
          </p>
        </div>
        {(() => {
          const { emojis, label } = streakBadge(user?.streak_days ?? 1);
          return (
            <span className="flex-shrink-0 px-2.5 py-1 bg-amber-500/15 border border-amber-500/30 rounded-full text-[12px] font-semibold text-amber-400 whitespace-nowrap">
              {emojis} {label}
            </span>
          );
        })()}
      </div>

      {/* Active user survey — red until answered, then editable while open */}
      <UserSurveyAlert facility={facility} isOnline={isOnline} />

      {/* YOUR TOPICS — hidden when the facility has no Topics tab, since each
          chip opens a topic detail view that would have nowhere to return to */}
      {topicsEnabled && (
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
            Your Topics
          </p>
          <div
            className="flex gap-2 overflow-x-auto pb-1 pr-7"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {visibleTopics.map((topic) => (
              <button
                key={topic.id}
                onClick={() => onOpenTopic?.(topic)}
                className="flex-shrink-0 flex items-center gap-1.5 px-4 py-[9px] rounded-full text-[13.5px] font-semibold bg-slate-800 border border-slate-700 text-slate-300 hover:border-cyan-500/60 hover:text-cyan-300 transition-colors whitespace-nowrap"
              >
                {topic.hot && (
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                )}
                {topic.developing && (
                  <span className="bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none flex-shrink-0">
                    NEW
                  </span>
                )}
                {topic.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Location lookup bar + LOG AN INTERACTION label */}
      <div>
        <div
          role="button"
          tabIndex={0}
          onClick={locLookup}
          onKeyDown={(e) => e.key === "Enter" && locLookup()}
          className="flex items-center gap-[13px] px-[13px] py-[10px] rounded-[13px] border border-cyan-500/35 cursor-pointer mb-[14px]"
          style={{
            background:
              "linear-gradient(to right, rgb(6 182 212 / 0.10), rgb(37 99 235 / 0.06)), #1e293b",
            boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
          }}
        >
          <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-cyan-500/15 border border-cyan-500/25">
            <MapPin size={17} className="text-cyan-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13.5px] font-extrabold text-white whitespace-nowrap overflow-hidden text-ellipsis">
              Look up this location
            </p>
            <p
              className={`text-[11px] font-medium whitespace-nowrap overflow-hidden text-ellipsis mt-px ${
                locError ? "text-amber-400" : "text-slate-400"
              }`}
            >
              {locError || "Uses your current location"}
            </p>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              searchLookup();
            }}
            className="flex-shrink-0 w-[34px] h-[34px] flex items-center justify-center rounded-lg border border-slate-600/50 bg-slate-800/60"
            aria-label="Search by name or address"
          >
            <Search size={17} className="text-slate-400" />
          </button>
        </div>
        {/* The lookup bar above stays regardless — it belongs to the Lookup
            tab, not to logging. Only the logger heading is switched. */}
        {logInteractionEnabled && (
          <p
            className="text-[11.5px] font-bold uppercase text-slate-400 tracking-[1.4px]"
            style={{ marginTop: "-4px", marginBottom: "10px" }}
          >
            Log an Interaction
          </p>
        )}
      </div>

      {/* 1×4 log tile row */}
      {logInteractionEnabled && (
        <div
          className="grid grid-cols-4 gap-[6px]"
          style={{ marginBottom: 12 }}
        >
          {LOG_TILES.map(
            ({
              type,
              label,
              icon: Icon,
              count,
              tcRgb: [r, g, b],
              textClass,
            }) => (
              <button
                key={type}
                onClick={() => onTapLog(type)}
                className="relative overflow-hidden rounded-[12px] text-center transition-transform duration-100 active:scale-[0.97]"
                style={{
                  padding: "8px 4px",
                  background: `linear-gradient(160deg, rgba(${r},${g},${b},0.24), rgba(${r},${g},${b},0.10)), #1e293b`,
                  border: `1px solid rgba(${r},${g},${b},0.42)`,
                  boxShadow: `0 4px 13px rgba(0,0,0,0.32), inset 0 1px 0 rgba(${r},${g},${b},0.30)`,
                }}
              >
                <span
                  className="absolute font-bold text-[9px] leading-none"
                  style={{
                    top: 7,
                    right: 8,
                    color: `rgba(${r},${g},${b},0.70)`,
                  }}
                >
                  {count}
                </span>
                <div
                  className="mx-auto flex items-center justify-center rounded-xl mb-[5px]"
                  style={{
                    width: 28,
                    height: 28,
                    background: `rgba(${r},${g},${b},0.18)`,
                  }}
                >
                  <Icon
                    size={16}
                    className={textClass}
                    style={{
                      filter: `drop-shadow(0 1px 3px rgba(${r},${g},${b},0.40))`,
                    }}
                  />
                </div>
                <span className="text-[11px] font-extrabold text-white leading-tight">
                  {label}
                </span>
              </button>
            ),
          )}
        </div>
      )}

      {/* Catch-all row */}
      {logInteractionEnabled && (
        <button
          onClick={() => onTapLog("other")}
          className="w-full flex items-center gap-3 px-4 rounded-[12px] border border-dashed border-slate-600/60 text-slate-400 text-[13px] font-semibold"
          style={{ paddingTop: 14, paddingBottom: 14, marginBottom: 24 }}
        >
          <Edit3 size={15} className="text-slate-500 flex-shrink-0" />
          Something else — note, event, other contact
        </button>
      )}

      {/* Upcoming events widget */}
      {upcomingEvents.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
            Upcoming Events
          </p>
          <div className="space-y-2">
            {upcomingEvents.slice(0, 2).map((ev) => (
              <UpcomingEventCard
                key={ev.id}
                ev={ev}
                facility={facility}
                isOnline={isOnline}
              />
            ))}
          </div>
        </div>
      )}

      {/* Today's logged activity. Fed only by the logger above, so with the
          feature off it could never fill — it would sit here permanently
          reading "No activity logged yet today" under a prompt to tap a
          button that is no longer on screen. */}
      {logInteractionEnabled && (
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
            Up Next
          </p>
          <div className="bg-slate-800 border border-slate-700 rounded-xl divide-y divide-slate-700">
            {activity.length === 0 ? (
              <div className="flex items-center gap-3 px-4 py-3">
                <Activity size={16} className="text-slate-500 flex-shrink-0" />
                <span className="text-sm text-slate-400">
                  No activity logged yet today
                </span>
              </div>
            ) : (
              activity.map((item, i) => {
                const Icon = typeIconMap[item.type] || Activity;
                const timeStr = new Date(item.ts).toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "2-digit",
                });
                return (
                  <div key={i} className="flex items-start gap-3 px-4 py-3">
                    <Icon
                      size={16}
                      className="text-slate-400 flex-shrink-0 mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">
                        {item.label} — {item.name}
                      </p>
                      {item.note && (
                        <p className="text-xs text-slate-500 truncate">
                          {item.note}
                        </p>
                      )}
                      {item.audioStatus === "error" && (
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-xs text-red-400">
                            Audio processing failed
                          </p>
                          <button
                            type="button"
                            onClick={() => handleRetryUpload(item)}
                            disabled={retryingKey === item.audioKey}
                            className="text-xs text-cyan-400 hover:text-cyan-300 disabled:opacity-50"
                          >
                            {retryingKey === item.audioKey
                              ? "Uploading…"
                              : "Retry upload"}
                          </button>
                        </div>
                      )}
                      {item.hasAudio &&
                        playingKey === item.audioKey &&
                        playingUrl && (
                          <audio
                            src={playingUrl}
                            controls
                            autoPlay
                            className="w-full h-8 mt-1.5 rounded"
                            onEnded={() => {
                              URL.revokeObjectURL(playingUrl);
                              setPlayingKey(null);
                              setPlayingUrl(null);
                            }}
                          />
                        )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {item.hasAudio && (
                        <button
                          type="button"
                          onClick={() => handlePlay(item.audioKey)}
                          title="Play recording"
                          className={`p-1.5 rounded-full transition-colors ${
                            playingKey === item.audioKey
                              ? "bg-cyan-600"
                              : "bg-slate-700 hover:bg-cyan-700"
                          }`}
                        >
                          <Play size={10} className="text-white fill-white" />
                        </button>
                      )}
                      <span className="text-xs text-slate-600">{timeStr}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          {activity.length === 0 && (
            <p className="text-xs text-slate-600 text-center mt-3">
              Tap "Log Interaction" to record your first contact today.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// CHAT TAB — full RAG chat, inherits Client Portal customizations
